import type { AnalysisSnapshot, MeshSnapshot } from './types';

/**
 * Merge per-mesh snapshots into a single index space so region-growing can cross
 * mesh boundaries (chunked/multi-part models). Pure: no Babylon. Vertices without UVs are
 * filled with NaN (the texel-density pass treats those triangles as uncovered).
 */
export function mergeSnapshots(snapshots: MeshSnapshot[]): AnalysisSnapshot {
  let vertexCount = 0;
  let triangleCount = 0;
  for (const s of snapshots) {
    vertexCount += s.positions.length / 3;
    triangleCount += s.indices.length / 3;
  }

  const positions = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const indices = new Uint32Array(triangleCount * 3);
  const triMeshIndex = new Uint32Array(triangleCount);
  const meshTriOffset = new Uint32Array(snapshots.length);
  const meshVertexOffset = new Uint32Array(snapshots.length);
  const meshes: AnalysisSnapshot['meshes'] = [];

  let vOff = 0;
  let tOff = 0;
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

  for (let m = 0; m < snapshots.length; m++) {
    const s = snapshots[m];
    const vCount = s.positions.length / 3;
    const tCount = s.indices.length / 3;
    meshVertexOffset[m] = vOff;
    meshTriOffset[m] = tOff;
    meshes.push({ textureSize: s.textureSize });

    positions.set(s.positions, vOff * 3);
    for (let v = 0; v < vCount; v++) {
      const x = s.positions[v * 3], y = s.positions[v * 3 + 1], z = s.positions[v * 3 + 2];
      if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
      if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
      if (s.uvs) {
        uvs[(vOff + v) * 2] = s.uvs[v * 2];
        uvs[(vOff + v) * 2 + 1] = s.uvs[v * 2 + 1];
      } else {
        uvs[(vOff + v) * 2] = NaN;
        uvs[(vOff + v) * 2 + 1] = NaN;
      }
    }
    for (let i = 0; i < s.indices.length; i++) indices[tOff * 3 + i] = s.indices[i] + vOff;
    for (let t = 0; t < tCount; t++) triMeshIndex[tOff + t] = m;

    vOff += vCount;
    tOff += tCount;
  }

  const bboxDiag = Number.isFinite(minX)
    ? Math.sqrt((maxX - minX) ** 2 + (maxY - minY) ** 2 + (maxZ - minZ) ** 2)
    : 0;

  return {
    positions, indices, uvs, triMeshIndex, meshTriOffset, meshVertexOffset, meshes,
    vertexCount, triangleCount, bboxDiag,
  };
}
